import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ConfigurationPreview } from "../../api/configuration";
import { AdminGate } from "../../components/admin/AdminGate";
import { PageIntro } from "../../components/admin/PageIntro";
import styles from "./ConfigurationContent.module.css";
import { ConfigurationExportPanel } from "./ConfigurationExportPanel";
import { ConfigurationImportPanel } from "./ConfigurationImportPanel";
import { ConfigurationPreviewView } from "./ConfigurationPreviewView";
import { PreCreateAllocationsPanel } from "../../components/admin/PreCreateAllocationsPanel";

const ConfigurationPermission = "configuration.import-export";

/**
 * The Configuration tab of System Settings: export this instance's configuration to an encrypted file, and import one after previewing
 * it. Both halves sit behind `configuration.import-export`; a person without it sees the denied state and no controls. The preview
 * sits beneath the two panels, full width, because its table has too many columns to read beside the import form.
 */
export function ConfigurationContent(): React.JSX.Element {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<ConfigurationPreview | null>(null);

  return (
    <div>
      <PageIntro>{t("configAdmin.description")}</PageIntro>
      <AdminGate permissions={[ConfigurationPermission]} title={t("configAdmin.denied.title")} description={t("configAdmin.denied.description")}>
        <div className={styles.page}>
          <div className={styles.sections}>
            <ConfigurationExportPanel />
            <ConfigurationImportPanel preview={preview} onPreviewChange={setPreview} />
          </div>
          {preview !== null && <ConfigurationPreviewView preview={preview} />}
          <PreCreateAllocationsPanel />
        </div>
      </AdminGate>
    </div>
  );
}
